import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { EPlantStatus } from "../../../../shared/oversight-core/enums/plant-status";
import { IScoutSolarPlantMeter } from "../../../../shared/oversight-core/interfaces/scout-solar-plant-meter";
import { IScoutSolarPlantView } from "../../../../shared/oversight-core/interfaces/scout-solar-plant-view";
import LeafletMapDetails from "../../../../shared/oversight-core/shared-components/leaflet-map-details/leaflet-map-details";
import WetherInfo from "../../../../shared/oversight-core/shared-components/wether-info/wether-info";
import AppBannerWithIcon from "../../../../shared/oversight-core/ui-elements/app-banner-with-icon/app-banner-with-icon";
import MaterialIcon, {
  MtIcon,
} from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import Helper from "../../../../shared/oversight-core/utils/helpers";
import { getRelativeTimeFromTimestamp } from "../../../../shared/oversight-core/utils/time-utils";
import { getScoutMeters } from "../view-site-helpers";
import styles from "./site-dashboard.module.scss";

interface IProps {
  solarPlant: IScoutSolarPlantView;
}

type MeterStyle = {
  icon: MtIcon;
  iconColor: string;
  backgroundColor: string;
  typeLabel: string;
};

const getMeterStyle = (type: string): MeterStyle => {
  if (type === "dc") {
    return {
      icon: "solar_power",
      iconColor: "#D08700",
      backgroundColor: "#F7F8EE",
      typeLabel: "DC",
    };
  }

  return {
    icon: "electric_meter",
    iconColor: "#3E6E8C",
    backgroundColor: "#EEF6F8",
    typeLabel: "AC",
  };
};

const formatMeterName = (meter: string): string => {
  return meter.replace(/^scout\d+_/i, "").replaceAll("_", " ");
};

const formatReading = (value: number | null | undefined, unit: string) => {
  if (value == null) {
    return "—";
  }
  if (!unit) {
    return Helper.roundTo2(value);
  }
  return `${Helper.roundTo2(value)} ${unit}`;
};

const MeterReadingRow = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => {
  return (
    <Row className="align-items-center justify-content-between mt-2">
      <Col className="text-light font-weight-400 font-size-12">{label}</Col>
      <Col className="col-auto text-dark font-weight-500 font-size-14">
        {value}
      </Col>
    </Row>
  );
};

const ScoutMeterCard = ({ meter }: { meter: IScoutSolarPlantMeter }) => {
  const style = getMeterStyle(meter.type);

  return (
    <div className={`${styles.infoCardContainer} p-4 h-100`}>
      <Row className="align-items-center">
        <Col className="col-auto pe-0">
          <div
            className="p-3 rounded-2"
            style={{ backgroundColor: style.backgroundColor }}
          >
            <MaterialIcon icon={style.icon} size={25} color={style.iconColor} />
          </div>
        </Col>
        <Col>
          <Row>
            <Col className="text-dark font-weight-600 font-size-16">
              {formatMeterName(meter.meter)}
            </Col>
          </Row>
          <Row>
            <Col className="text-light font-weight-400 font-size-12">
              {style.typeLabel}
              {meter.readingAt
                ? ` · ${getRelativeTimeFromTimestamp(meter.readingAt)}`
                : ""}
            </Col>
          </Row>
        </Col>
      </Row>
      <div className={`${styles.temperatureCard} mt-3 pt-2`}>
        <MeterReadingRow
          label="Voltage"
          value={formatReading(meter.voltage, "V")}
        />
        <MeterReadingRow
          label="Current"
          value={formatReading(meter.current, "A")}
        />
        <MeterReadingRow
          label="Power"
          value={formatReading(meter.power, "W")}
        />
        <MeterReadingRow
          label="Power Factor"
          value={formatReading(meter.powerFactor, "")}
        />
      </div>
    </div>
  );
};

const ScoutServiceErrorBanner = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => {
  if (!show) {
    return null;
  }

  return (
    <Row className="mb-4">
      <Col>
        <AppBannerWithIcon
          content={
            <Row>
              <Col className="text-white font-weight-400 font-size-12">
                API is temporarily down. Showing the last recorded data until
                live updates resume.
              </Col>
            </Row>
          }
          bannerVariant="red"
          icon="emergency_home"
          iconVariant="#FFFFFF"
          iconSize={18}
          button={
            <MaterialIcon
              icon="close"
              color="#ffffff"
              size={15}
              onClick={onClose}
              className="cursor-pointer"
            />
          }
        />
      </Col>
    </Row>
  );
};

const hasScoutCoordinates = (
  location?: IScoutSolarPlantView["location"],
): boolean => {
  if (!location) {
    return false;
  }
  return location.latitude != null && location.longitude != null;
};

const ScoutLocationCard = ({
  solarPlant,
}: {
  solarPlant: IScoutSolarPlantView;
}) => {
  const address = solarPlant.address?.trim();
  const location = solarPlant.location;
  const hasLocation = hasScoutCoordinates(location);

  if (!address && !hasLocation) {
    return null;
  }

  return (
    <Row className="mx-0">
      <Col className={`${styles.infoCardContainer} p-3`}>
        {address && (
          <Row className="mb-3">
            <Col className="text-dark font-weight-500 font-size-16">
              {address}
            </Col>
          </Row>
        )}
        {hasLocation && location && (
          <>
            <Row
              className={`${styles.todayBackground} align-items-center mx-0 p-3`}
            >
              <Col>
                <WetherInfo
                  longitude={Number(location.longitude)}
                  latitude={Number(location.latitude)}
                  hideOtherDays
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <LeafletMapDetails
                  locations={[
                    {
                      lat: Number(location.latitude),
                      lng: Number(location.longitude),
                      name: solarPlant.siteName || solarPlant.plantName,
                    },
                  ]}
                  height={150}
                  hidePopup
                />
              </Col>
            </Row>
          </>
        )}
      </Col>
    </Row>
  );
};

const ScoutAlertsBanner = ({
  solarPlant,
  show,
  onClose,
}: {
  solarPlant: IScoutSolarPlantView;
  show: boolean;
  onClose: () => void;
}) => {
  if (!show) {
    return null;
  }
  if (
    !solarPlant.solarPlantAlerts ||
    solarPlant.solarPlantAlerts.length === 0
  ) {
    return null;
  }
  if (solarPlant.plantStatus !== EPlantStatus.FAULT) {
    return null;
  }

  return (
    <Row className="mb-4">
      <Col>
        <AppBannerWithIcon
          content={
            <>
              {solarPlant.solarPlantAlerts.map((alert) => (
                <Row
                  key={`${alert.alertName}-${alert.startTime}-${alert.description}`}
                >
                  <Col className="col-auto text-white font-weight-400 font-size-12 pe-0">
                    {alert.alertName}&nbsp;&nbsp;&nbsp;|
                  </Col>
                  <Col className="text-white font-weight-400 font-size-12">
                    {alert.description}
                  </Col>
                </Row>
              ))}
            </>
          }
          bannerVariant="red"
          icon="emergency_home"
          iconVariant="#FFFFFF"
          iconSize={18}
          button={
            <MaterialIcon
              icon="close"
              color="#ffffff"
              size={15}
              onClick={onClose}
              className="cursor-pointer"
            />
          }
        />
      </Col>
    </Row>
  );
};

const ScoutLiveMeterReadings = ({
  meters,
}: {
  meters: IScoutSolarPlantMeter[];
}) => {
  return (
    <Row
      className={`${styles.siteStaticContainer} py-4 px-2 mx-0 h-100 position-relative`}
    >
      <Col>
        <Row className="mb-3">
          <Col className="text-dark font-weight-500 font-size-20">
            Live Meter Readings
          </Col>
        </Row>
        <Row className="g-4 row-cols-1 row-cols-xl-3">
          {meters.map((meter) => (
            <Col key={meter.meter}>
              <ScoutMeterCard meter={meter} />
            </Col>
          ))}
        </Row>
        {meters.length === 0 && (
          <Row>
            <Col className="text-light font-weight-400 font-size-14">
              No meter readings available
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
};

const ScoutSiteDashboard = ({ solarPlant }: IProps) => {
  const [isHideAlerts, setIsHideAlerts] = useState(false);
  const meters = getScoutMeters(solarPlant);
  const hideAlerts = () => setIsHideAlerts(true);
  const hasLocationDetails = Boolean(
    solarPlant.address?.trim() || hasScoutCoordinates(solarPlant.location),
  );

  return (
    <div className="position-relative">
      <ScoutServiceErrorBanner
        show={Boolean(solarPlant?.serviceError) && !isHideAlerts}
        onClose={hideAlerts}
      />
      <ScoutAlertsBanner
        solarPlant={solarPlant}
        show={!isHideAlerts}
        onClose={hideAlerts}
      />
      <Row>
        <Col className={hasLocationDetails ? "col-12 col-xl-9" : "col-12"}>
          <ScoutLiveMeterReadings meters={meters} />
        </Col>
        {hasLocationDetails && (
          <Col className="col-12 col-xl-3 mt-4 mt-xl-0">
            <ScoutLocationCard solarPlant={solarPlant} />
          </Col>
        )}
      </Row>
    </div>
  );
};

export default ScoutSiteDashboard;
